<template>
  <v-card>
    <v-card-title class="pt-0 pb-0">
      <v-row>
        <v-col cols="12" lg="4" class="pt-1 pb-0">
          <h5>{{ !invoiceObj.id ? $t('message.invoice.addInvoice') : $t('message.customer.invoice') }} {{ getInvoiceNumber }} {{ getStatusName }}</h5>
          <p v-if="invoiceObj.integrationerrormessage" class="error--text mb-0">ERP sync failed with error: {{ invoiceObj.integrationerrormessage }}</p>
        </v-col>
        <v-col cols="12" lg="8" class="text-end">
          <v-btn small color="primary" class="mr-2" @click="openAttachmentDialog" :disabled="invoiceObj.status !== 'draft'">
            {{$t('message.product.attachments')}}
            <span v-if="attachmentsCountLoading">&nbsp;(<span>
              <v-progress-circular
                indeterminate
                color="white"
                x-small
                :size="12"
                :width="2"
              ></v-progress-circular>
            </span>)</span>
            <span v-else>&nbsp;({{attachmentsCount}})</span>
          </v-btn>
          <template v-if="invoiceObj.status === 'completed'" @click="getCreditnoteconfirmation">
            <v-btn color="primary" small dark :loading="saveLoader" v-if="!isAllCredited" @click="creditNoteDialog = true" class="mr-2">{{$t('message.invoice.createCreditNote')}}</v-btn>
            <v-btn color="primary" small dark :loading="ehfLoader" class="mr-2" @click="ehfWarningHandler" v-if="isShowResendEhf()">{{ $t('message.invoice.resendEhf') }}</v-btn>
          </template>
          <template v-if="!$route.query.from">
            <v-menu top :close-on-content-click="true" v-if="invoiceObj.status === 'draft'">
              <template v-slot:activator="{ on }">
                <v-btn color="primary" class="mr-2" dark v-on="on" :loading="saveLoader" small>{{$t('message.invoice.saveAs')}}</v-btn>
              </template>
              <v-list>
                <v-list-item v-for="(item, index) in dropdownItems" :key="index" @click="item.callback">
                  <v-list-item-title>{{ item.text }}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
          <v-btn color="primary" v-if="isShowResendEmail()" small dark @click="resendDialogEmailHandler" :loading="emailLoader" class="mr-2">{{ $t('message.invoice.resendEmail') }}</v-btn>
          <v-btn color="success" v-if="invoiceObj.number && !invoiceObj.external_id" small dark @click="syncToErp" :loading="syncToErpLoading" class="mr-2">Sync to ERP</v-btn>
          <v-btn color="info" class="mr-2" @click="downloadInvoice(false, '')" :loading="previewLoader" v-if="invoiceObj.id" small>{{$t('message.deviations.downloadPDF') }}</v-btn>
          <v-btn small color="warning" class="mr-2" @click="$router.go(-1)">{{$t('message.common.backToList')}}</v-btn>
        </v-col>
      </v-row>
    </v-card-title>
    <v-progress-linear :active="loading" :indeterminate="loading" absolute color="primary accent-4" v-if="loading"></v-progress-linear>
    <v-divider v-else></v-divider>
    <v-card-text class="pt-1 pb-1">
      <v-form ref="invoiceForm" onsubmit="return false;" autocomplete="off">
        <v-row>
          <v-col cols="12" lg="6">
            <v-row>
              <v-col cols="6" class="pa-1">
                <v-textarea dense hide-details outlined rows="4" readonly v-model="invoiceObj.invoicetodetail" :label="$t('message.orders.invoiceTo')"></v-textarea>
              </v-col>
              <v-col cols="6" class="pa-1">
                <v-textarea dense hide-details outlined rows="4" readonly v-model="invoiceObj.orderfordetail" :label="$t('message.orders.orderFor')"></v-textarea>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" lg="6">
            <v-row>
              <v-col cols="12" sm="6" md="3" lg="4" class="pa-1">
                <v-autocomplete v-model="invoiceObj.payment_term" :items="listOfPaymentTerms" item-text="name" item-value="days"
                  :label="$t('message.invoice.pmtTerms')" outlined dense hide-details :disabled="disableFields"
                  @change="invoiceEdited">
                </v-autocomplete>
              </v-col>
              <v-col cols="12" sm="6" md="3" lg="4" class="pa-1">
                <v-menu :close-on-content-click="false" v-model="invoicedDate" transition="scale-transition" offset-y min-width="290px" :disabled="splDisableFields">
                  <template v-slot:activator="{ on }">
                    <v-text-field outlined hide-details v-model="invoiceObj.invoice_date" :rules="$_requiredValidation" clearable :label="$t('message.invoice.invoicedDate')"
                      prepend-icon="mdi-calendar" readonly v-on="on"
                      @change="invoiceEdited"
                      @click:clear="$nextTick(() => invoicedDatePicker = null)" dense :disabled="splDisableFields">
                    </v-text-field>
                    </template>
                    <v-date-picker color="primary" @input="invoicedDate = false" v-model="invoicedDatePicker" :disabled="splDisableFields"
                      first-day-of-week='1'></v-date-picker>
                  </v-menu>
              </v-col>
              <v-col cols="12" sm="6" md="3" lg="4" class="pa-1">
                <v-menu :close-on-content-click="false" v-model="invoiceDueDate" transition="scale-transition" offset-y min-width="290px" :disabled="disableFields">
                  <template v-slot:activator="{ on }">
                    <v-text-field outlined hide-details v-model="invoiceObj.due_date" :rules="$_requiredValidation" clearable :disabled="disableFields"
                      :label="$t('message.invoice.duedate')"
                      @change="invoiceEdited"
                      prepend-icon="mdi-calendar" readonly v-on="on" @click:clear="$nextTick(() => invoiceDuePicker = null)" dense>
                    </v-text-field>
                    </template>
                    <v-date-picker color="primary" @input="invoiceDueDate = false" v-model="invoiceDuePicker" :disabled="disableFields"
                      first-day-of-week='1'></v-date-picker>
                  </v-menu>
              </v-col>
              <v-col cols="12" sm="6" md="3" lg="4" class="pa-1">
                <v-menu :close-on-content-click="false" v-model="invoicedCompletedDate" transition="scale-transition" offset-y min-width="290px" :disabled="disableFields">
                  <template v-slot:activator="{ on }">
                    <v-text-field outlined hide-details v-model="invoiceObj.completed_date" clearable :disabled="disableFields"
                      :label="$t('message.invoice.completedDate')"
                      @change="invoiceEdited"
                      prepend-icon="mdi-calendar" readonly v-on="on" @click:clear="$nextTick(() => invoicedCompletedPicker = null)" dense></v-text-field>
                    </template>
                    <v-date-picker color="primary" @input="invoicedCompletedDate = false" v-model="invoicedCompletedPicker" :disabled="disableFields"
                      first-day-of-week='1'></v-date-picker>
                  </v-menu>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" lg="6">
            <v-row>
              <v-col cols="12" lg="4" md="2" xs="12" sm="4" class="pa-1">
                <v-text-field outlined hide-details dense v-model="invoiceObj.order_salesreference" :label="$t('message.orders.salesReference')" :disabled="splDisableFields"></v-text-field>
              </v-col>
              <v-col cols="12" lg="4" md="2" xs="12" sm="4" class="pa-1">
                <v-text-field v-model="invoiceObj.order_yourordernumber" outlined dense :label="$t('message.orders.yourOrderNumber')" hide-details
                @change="invoiceEdited"
                :disabled="splDisableFields"></v-text-field>
              </v-col>
              <v-col cols="12" lg="4" md="2" xs="12" sm="4" class="pa-1">
                <v-text-field v-model="invoiceObj.your_reference" outlined dense :label="$t('message.invoice.yourReference')" hide-details
                @change="invoiceEdited"
                :disabled="splDisableFields"></v-text-field>
              </v-col>
              <v-col cols="12" lg="4" md="2" xs="12" sm="4" class="pa-1">
                <v-text-field v-model="invoiceObj.your_contact" outlined dense :label="$t('message.invoice.yourContact')" hide-details
                @change="invoiceEdited"
                :disabled="splDisableFields"></v-text-field>
              </v-col>
              <v-col cols="12" lg="4" md="2" xs="12" sm="4" class="pa-1">
                <v-autocomplete v-model="invoiceObj.our_reference" :items="usersList" item-text="name" item-value="id"
                  :label="$t('message.invoice.ourReference')" outlined dense hide-details :disabled="splDisableFields"
                  @change="invoiceEdited">
                </v-autocomplete>
              </v-col>
              <v-col cols="12" lg="4" md="2" xs="12" sm="4" class="pa-1">
                <v-autocomplete v-model="invoiceObj.deliveryterms_id" :items="listOfDeliveryTerms" item-text="name" item-value="id"
                  :label="$t('message.order.deliveryTerm')" outlined dense hide-details :disabled="disableFields"
                  @change="invoiceEdited">
                </v-autocomplete>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" lg="6">
            <v-row>
              <v-col cols="12" class="pa-1">
                <v-textarea v-model="invoiceObj.tasks_performed" :disabled="splDisableFields" outlined rows="3" :label="$t('message.common.comments')" hide-details></v-textarea>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-divider class="mt-1"></v-divider>
        <v-simple-table dense class="dense_table invoiceProducts invoiceitems" fixed-header>
          <template v-slot:default>
            <thead>
              <tr>
                <th :class="header.class" v-for="(header, index) in productHeaders" :key="index">{{header.text}}
                  <span style="color:red"> {{ header.showRequired ? '(*)' : '' }}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(element, index) in listOfInvoiceItems">
                <tr :key="`${index}_item`" :style="{ background: (element.color ? element.color : ((index  % 2) === 0) ? '#f8f8ff' : '#ededfb')}">
                  <td width="9%" v-if="element.product_number">
                    <v-text-field hide-details full-width readonly v-model="element.product_number"></v-text-field>
                  </td>
                  <td width="9%" v-else>
                    <v-text-field hide-details full-width :readonly="element.is_hour_or_usedproduct ? true : false" v-model="element.number" :disabled="disableFields"
                      :rules="element.number ? [(v) =>(v && v.length <= 10) || ``] : []"
                      ></v-text-field>
                  </td>
                  <td width="20%">
                    <v-text-field @click.stop="" v-model="element.description" hide-details full-width :placeholder="$t('message.common.typeHere')"
                     :disabled="disableFields" class="commentsArea textfieldstyle">
                      <template v-slot:append>
                        <v-icon class="mt-0" :disabled="invoiceObj.status === 'draft' ? false : !!element.comments" @click.stop="hideAndShowComments(element, index)">mdi-comment-text</v-icon>
                      </template>
                    </v-text-field>
                  </td>
                  <td width="6%">
                    <template v-if="!element.product_isinformation">
                      <v-text-field @click.stop="" v-model="element.qty" hide-details full-width :placeholder="$t('message.common.typeHere')"
                        :rules="element.qty ? $_norwegianNumberMinusValidation : $_zeroSelectValidation" class="align-right" :style="element.qty ? 'background: none' : ''"
                        @change="doCalculation(index)" :disabled="disableFields">
                      </v-text-field>
                    </template>
                  </td>
                  <td width="5%">
                    <template v-if="!element.product_isinformation">
                      <v-select @click.stop="" :disabled="disableFields" v-model="element.unit_id" :items="listOfUnits" item-text="name"
                      item-value="id" class="align-right" hide-details full-width></v-select>
                      </template>
                  </td>
                  <td width="7%">
                    <template v-if="!element.product_isinformation">
                      <v-text-field @click.stop="" v-model="element.net_price" hide-details full-width :placeholder="$t('message.common.typeHere')" :style="element.net_price ? 'background: none' : ''"
                        :rules="element.net_price ? $_norwegianNumberValidation : $_zeroSelectValidation" class="align-right"
                        @change="doCalculation(index)" :disabled="disableFields">
                      </v-text-field>
                    </template>
                  </td>
                  <td width="7%">
                    <template v-if="!element.product_isinformation">
                      <v-text-field @click.stop="" v-model="element.base_price" hide-details full-width :placeholder="$t('message.common.typeHere')"
                        :rules="element.base_price ? $_norwegianNumberValidation : $_zeroSelectValidation" class="align-right"
                        @change="doCalculation(index)" :disabled="disableFields" :style="element.base_price ? 'background: none' : ''">
                      </v-text-field>
                    </template>
                  </td>
                  <td width="6%">
                    <template v-if="!element.product_isinformation">
                      <v-text-field @click.stop="" v-model="element.discount" hide-details full-width :placeholder="$t('message.common.typeHere')" :rules="element.discount ? $_discountValidation : []"
                        class="align-right" @change="doCalculation(index)" :disabled="disableFields">
                      </v-text-field>
                    </template>
                  </td>
                  <td width="8%">
                    <template v-if="!element.product_isinformation">
                      <v-text-field v-model="element.sum_ex_vat" class="align-right" readonly hide-details full-width></v-text-field>
                    </template>
                  </td>
                  <td width="6%">
                    <template v-if="!element.product_isinformation">
                      <v-select @click.stop="" v-model="element.vat" full-width
                     @change="doCalculation(index)" class="align-right pa-0 pl-2" item-text="text" item-value="value" :disabled="disableFields" hide-details :items="listOfVAT"></v-select>
                    </template>
                  </td>
                  <td width="7%">
                    <template v-if="!element.product_isinformation">
                      <v-text-field v-model="element.price" readonly hide-details full-width :placeholder="$t('message.common.typeHere')"
                        class="align-right">
                      </v-text-field>
                    </template>
                  </td>
                  <td width="8%" class="text-right">
                    <template v-if="!element.product_isinformation">
                      {{ element.cost }}
                    </template>
                  </td>
                  <td width="8%" class="text-right">
                    <template v-if="!element.product_isinformation">
                      {{ element.db }}
                    </template>
                  </td>
                  <td width="8%" class="text-right">
                    <template v-if="!element.product_isinformation">
                      {{ element.dg }}
                    </template>
                  </td>
                </tr>
                <tr v-if="invoiceObj.status === 'draft' ? element.show_comments : !!element.comments" :style="{ background: element.selected ? '#80808038' : element.color }" :key="`${index}_comments`">
                  <td :colspan="9" class="pa-1 pl-0 pr-0">
                    <v-textarea :rows="1" :disabled="invoiceObj.status === 'draft' ? false : disableFields" auto-grow
                  class="comments_invoice mt-1" hide-details outlined v-model="element.comments"></v-textarea>
                  </td>
                  <td :colspan="4">
                  </td>
                </tr>
              </template>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="6"></td>
                <td class="text-right" style="padding: 0 2px"><b>{{ $t('message.invoice.sum') }}</b></td>
                <td class="text-right" :class="+$formatter.replaceCommaWithDot(totalValues.sum) <= 0 ? 'invalidTotal' : ''" style="padding: 0 2px">
                  <b>{{ totalValues.sum }}</b>
                </td>
                <td class="text-right" :class="+$formatter.replaceCommaWithDot(totalValues.sum) <= 0 ? 'invalidTotal' : ''" style="padding: 0 2px"><b>{{ totalValues.mva }}</b></td>
                <td class="text-right" :class="+$formatter.replaceCommaWithDot(totalValues.sum) <= 0 ? 'invalidTotal' : ''" style="padding: 0 2px"><b>{{ totalValues.invoiceTotal }}</b></td>
                <td class="text-right" :class="+$formatter.replaceCommaWithDot(totalValues.sum) <= 0 ? 'invalidTotal' : ''" style="padding: 0 2px"><b>{{ totalValues.cost }}</b></td>
                <td class="text-right" :class="+$formatter.replaceCommaWithDot(totalValues.sum) <= 0 ? 'invalidTotal' : ''" style="padding: 0 2px"><b>{{ totalValues.db }}</b></td>
                <td class="text-right" :class="+$formatter.replaceCommaWithDot(totalValues.sum) <= 0 ? 'invalidTotal' : ''" style="padding: 0 2px"><b>{{ totalValues.dg_percentage }}</b></td>
              </tr>
            </tfoot>
          </template>
        </v-simple-table>
        <br />
        <v-row row wrap v-if="invoiceObj.status === 'completed'">
          <v-col cols="12" v-if="invoiceObj.completion_type === 2 && invoiceObj.invoicedownloadedby">
            <span class="subtitle-1 font-weight-bold">{{ $t('message.invoice.invoiceDownloadedBy',
               { user: invoiceObj.invoicedownloadedbyname || '',
                date: $formatter.formatDate(invoiceObj.invoicedownloadedon, 'DD.MM.YYYYTHH:mm:ss', 'DD.MM.YYYY HH:mm:ss') }) }}
            </span>
          </v-col>
          <v-col cols="12" v-else-if="invoiceObj.completion_type === 3 && invoiceObj.ehfsentcount === 1">
            <span class="subtitle-1 font-weight-bold">{{ $t('message.invoice.invoiceEHFBy',
               { user: invoiceObj.ehfsentbyname || '',
                date: $formatter.formatDate(invoiceObj.ehfsenton, 'DD.MM.YYYYTHH:mm:ss', 'DD.MM.YYYY HH:mm:ss') }) }}
            </span>
          </v-col>
          <v-col cols="12" v-else-if="invoiceObj.completion_type === 3 && invoiceObj.ehfsentcount >= 2">
            <span class="subtitle-1 font-weight-bold">{{ $t('message.invoice.invoiceEHFResentBy',
               { user: invoiceObj.ehfsentbyname || '',
                date: $formatter.formatDate(invoiceObj.ehfsenton, 'DD.MM.YYYYTHH:mm:ss', 'DD.MM.YYYY HH:mm:ss') }) }}
            </span>
          </v-col>
          <v-col cols="12" v-else>
            <span class="subtitle-1 font-weight-bold">{{$t('message.invoice.invoiceCreatedBy')}} {{ `${invoiceObj.user_firstname || ''} ${invoiceObj.user_lastname || '' }` }} {{$t('message.invoice.onDate')}} {{ $formatter.formatDate(invoiceObj.modified_on, 'DD.MM.YYYYTHH:mm:ss', 'DD.MM.YYYY HH:mm:ss')}}</span>
          </v-col>
          <v-col cols="12" v-if="(invoiceObj.completion_type === 4 || invoiceObj.completion_type === 2) && invoiceObj.invoiceemailedby">
            <span class="subtitle-1 font-weight-bold">{{ $t('message.invoice.invoiceEmailedBy',
               { user: invoiceObj.invoiceemailedbyname || '',
                email: invoiceObj.invoiceemailedto || '',
                date: $formatter.formatDate(invoiceObj.invoiceemailedon, 'DD.MM.YYYYTHH:mm:ss', 'DD.MM.YYYY HH:mm:ss') }) }}
            </span>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
    <v-dialog v-model="confirmDialog" width="400" persistent>
      <v-card>
        <v-card-title>
          {{$t('message.invoice.confirmation')}}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          {{$t('message.invoice.creditConfirm')}}
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-layout>
            <v-flex class="text-center">
              <v-btn color="primary" @click="createCreditNote" class="mr-3"> {{$t('message.common.yes')}}</v-btn>
              <v-btn color="error" @click="confirmDialog = false"> {{$t('message.common.no')}}</v-btn>
            </v-flex>
          </v-layout>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="creditNoteDialog" width="400" persistent>
      <v-card>
        <v-card-title class="pa-2">
          {{$t('message.invoice.chooseorAbort')}}
          <v-spacer></v-spacer>
          <v-btn fab x-small icon @click="creditNoteDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-actions class="pa-3">
          <!-- <v-layout>
            <v-flex class="text-center">
              <v-btn class="mx-2" color="primary" @click="createCreditNote('full')"> {{ $t('message.invoice.fullCredit') }}</v-btn>
              <v-btn class="mx-2" color="primary" @click="createCreditNote('partial')"> {{ $t('message.invoice.partialCredit') }}</v-btn>
              <v-btn class="mx-2" color="error" @click="creditNoteDialog = false"> {{ $t('message.invoice.abort') }}</v-btn>
            </v-flex>
          </v-layout> -->
          <v-layout row wrap justify-space-between justify-space-around class="pa-3 mt-1">
            <template v-for="(option, index) in creditNoteOptions">
              <v-flex xs12 :class="{'py-2': $vuetify.breakpoint.xsOnly}" :key="index">
                <v-card :class="{'mb-3': $vuetify.breakpoint.smAndUp}"  @click="option.type === 'abort' ? creditNoteDialog = false : createCreditNote(option.type)">
                  <v-card-text class="pa-0">
                    <v-container fluid class="pa-0 grid-list-xl">
                      <div class="layout row ma-0">
                        <div class="pa-0 flex xs3" :class="option.color">
                          <div class="justify-center align-center text-sm-center py-4">
                            <v-icon size="30px" color="white">{{ option.icon }}</v-icon>
                          </div>
                        </div>
                        <div class="pa-0 flex xs9 pa-1">
                          <div class="body-1 font-weight-bold">{{option.title}}</div>
                          <span class="caption">{{option.content}}</span>
                        </div>
                      </div>
                    </v-container>
                  </v-card-text>
                </v-card>
              </v-flex>
            </template>
          </v-layout>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showEmailDialog" max-width="500px">
      <v-form ref="emailForm" onsubmit="return false;">
        <v-card>
          <v-card-title>
            {{$t('message.unimicro.sendReport')}}
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="mt-4">
            <v-text-field outlined textarea :label="$t('message.invoice.invoiceEmail')" v-model="emailIds" :rules="$_requiredValidation" hide-details></v-text-field>
            <span>{{ $t('message.common.multiEmailHint') }}</span>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="success" @click="saveAndEmailHandler(4)" :loading="saveLoader">{{$t('message.common.send')}}</v-btn>
            <v-btn color="error" @click.stop="showEmailDialog=false">{{$t('message.common.close')}}</v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>
    <v-dialog v-model="createNoteProductsDialog" fullscreen>
      <v-card>
        <v-toolbar
          dark
          color="primary"
        >
          <v-btn
            icon
            dark
            @click="createNoteProductsDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>{{ $t('message.invoice.selectProducts') }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn
              text
              :disabled="!createButtonEnabled"
              @click="createPartialCreditNote"
            >
              {{ $t('message.invoice.createCreditNote') }}
            </v-btn>
            <v-btn
              text
              @click="createNoteProductsDialog = false"
            >
              {{ $t('message.invoice.abort') }}
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-card-text class="mt-2">
          <v-simple-table dense class="dense_table invoiceProducts invoiceitems" fixed-header>
            <template v-slot:default>
              <thead>
                <tr>
                  <th></th>
                  <th :class="header.class" v-for="(header, index) in productHeaders" :key="index">{{header.text}}
                    <span style="color:red"> {{ header.showRequired ? '(*)' : '' }}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <template v-for="(element, index) in allInvoiceItems">
                  <tr :key="`${index}_item`" :style="{ background: (element.color ? element.color : ((index  % 2) === 0) ? '#f8f8ff' : '#ededfb')}">
                    <td>
                      <v-checkbox @change="checkboxChanged" dense hide-details color="primary" v-model="element.isSelected" class="mt-0 pa-0"></v-checkbox>
                    </td>
                    <td width="9%" v-if="element.product_number">
                      <v-text-field hide-details full-width readonly v-model="element.product_number"></v-text-field>
                    </td>
                    <td width="9%" v-else>
                      <v-text-field hide-details full-width :readonly="element.is_hour_or_usedproduct ? true : false" v-model="element.number" :disabled="disableFields"
                        :rules="element.number ? [(v) =>(v && v.length <= 10) || ``] : []"
                        ></v-text-field>
                    </td>
                    <td width="20%">
                      <v-text-field @click.stop="" v-model="element.description" hide-details full-width :placeholder="$t('message.common.typeHere')"
                      :disabled="disableFields" class="commentsArea textfieldstyle">
                        <template v-slot:append>
                          <v-icon class="mt-0" :disabled="!!element.comments" @click.stop="hideAndShowComments(element, index)">mdi-comment-text</v-icon>
                        </template>
                      </v-text-field>
                    </td>
                    <td width="6%">
                      <template v-if="!element.product_isinformation">
                        <v-text-field @click.stop="" v-model="element.qty" hide-details full-width :placeholder="$t('message.common.typeHere')"
                          :rules="element.qty ? $_norwegianNumberMinusValidation : $_zeroSelectValidation" class="align-right" :style="element.qty ? 'background: none' : ''"
                          @change="doCalculation(index)" :disabled="disableFields">
                        </v-text-field>
                      </template>
                    </td>
                    <td width="5%">
                      <template v-if="!element.product_isinformation">
                        <v-select @click.stop="" :disabled="disableFields" v-model="element.unit_id" :items="listOfUnits" item-text="name"
                        item-value="id" class="align-right" hide-details full-width></v-select>
                      </template>
                    </td>
                    <td width="7%">
                      <template v-if="!element.product_isinformation">
                        <v-text-field @click.stop="" v-model="element.net_price" hide-details full-width :placeholder="$t('message.common.typeHere')" :style="element.net_price ? 'background: none' : ''"
                          :rules="element.net_price ? $_norwegianNumberValidation : $_zeroSelectValidation" class="align-right"
                          @change="doCalculation(index)" :disabled="disableFields">
                        </v-text-field>
                      </template>
                    </td>
                    <td width="7%">
                      <template v-if="!element.product_isinformation">
                        <v-text-field @click.stop="" v-model="element.base_price" hide-details full-width :placeholder="$t('message.common.typeHere')"
                          :rules="element.base_price ? $_norwegianNumberValidation : $_zeroSelectValidation" class="align-right"
                          @change="doCalculation(index)" :disabled="disableFields" :style="element.base_price ? 'background: none' : ''">
                        </v-text-field>
                      </template>
                    </td>
                    <td width="6%">
                      <template v-if="!element.product_isinformation">
                        <v-text-field @click.stop="" v-model="element.discount" hide-details full-width :placeholder="$t('message.common.typeHere')" :rules="element.discount ? $_discountValidation : []"
                          class="align-right" @change="doCalculation(index)" :disabled="disableFields">
                        </v-text-field>
                      </template>
                    </td>
                    <td width="8%">
                      <template v-if="!element.product_isinformation">
                        <v-text-field v-model="element.sum_ex_vat" class="align-right" readonly hide-details full-width></v-text-field>
                      </template>
                    </td>
                    <td width="6%">
                      <template v-if="!element.product_isinformation">
                        <v-select @click.stop="" v-model="element.vat" full-width
                        @change="doCalculation(index)" class="align-right pa-0 pl-2" item-text="text" item-value="value" :disabled="disableFields" hide-details :items="listOfVAT"></v-select>
                      </template>
                    </td>
                    <td width="7%">
                      <template v-if="!element.product_isinformation">
                        <v-text-field v-model="element.price" readonly hide-details full-width :placeholder="$t('message.common.typeHere')"
                          class="align-right">
                        </v-text-field>
                      </template>
                    </td>
                    <td width="8%" class="text-right">
                      <template v-if="!element.product_isinformation">
                        {{ element.cost }}
                      </template>
                    </td>
                    <td width="8%" class="text-right">
                      <template v-if="!element.product_isinformation">
                        {{ element.db }}
                      </template>
                    </td>
                    <td width="8%" class="text-right">
                      <template v-if="!element.product_isinformation">
                        {{ element.dg }}
                      </template>
                    </td>
                  </tr>
                  <tr v-if="element.comments" :style="{ background: element.selected ? '#80808038' : element.color }" :key="`${index}_comments`">
                    <td :colspan="9" class="pa-1 pl-0 pr-0">
                      <v-textarea :rows="1" :disabled="disableFields" auto-grow
                    class="comments_invoice mt-1" hide-details outlined v-model="element.comments"></v-textarea>
                    </td>
                    <td :colspan="4">
                    </td>
                  </tr>
                </template>
              </tbody>
              <tfoot>
                <tr>
                  <td></td>
                  <td colspan="6"></td>
                  <td class="text-right" style="padding: 0 2px"><b>{{ $t('message.invoice.sum') }}</b></td>
                  <td class="text-right" style="padding: 0 2px"><b>
                    {{ totalValues.sum }}</b>
                  </td>
                  <td class="text-right" style="padding: 0 2px"><b>{{ totalValues.mva }}</b></td>
                  <td class="text-right" style="padding: 0 2px"><b>{{ totalValues.invoiceTotal }}</b></td>
                  <td class="text-right" style="padding: 0 2px"><b>{{ totalValues.cost }}</b></td>
                  <td class="text-right" style="padding: 0 2px"><b>{{ totalValues.db }}</b></td>
                  <td class="text-right" style="padding: 0 2px"><b>{{ totalValues.dg_percentage }}</b></td>
                </tr>
              </tfoot>
            </template>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>
    <!-- Attachment adding dialog -->
    <v-dialog v-model="attachmentDialog" persistent max-width="1150px" class="mt-0">
      <v-form ref="shareForm" onSubmit="return false;">
        <v-card>
          <v-card-title>
            {{ $t('message.product.attachments') }}
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-2">
            <v-col cols="12">
              <attach_share v-if="attachmentDialog" v-model="filelist" :filelist="filelist" :sharepoint="sharepointObj"></attach_share>
            </v-col>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="success" id="addAttachment" :loading="attachmentLoading" @click="addAttachmentsToSP">{{ $t('message.common.add') }}</v-btn>
            <v-btn color="error" id="close" @click="closeAttachmentDialog">{{ $t('message.common.close') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>
    <!-- Share Dialog -->
    <v-dialog v-model="shareDialog" persistent max-width="1150px" class="mt-0">
      <v-form ref="shareForm" onSubmit="return false;">
        <v-card>
          <v-card-title>
            {{ $t('message.order.sendInvoice') }}
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-2">
            <v-col cols="12">
              <v-row>
                <v-text-field outlined dense v-model="emailIds" ref="autoFocus" :label="$t('message.customer.email')" :rules="$_requiredValidation"
                  persistent-hint :hint="$t('message.common.add_extra_mails')"></v-text-field>
              </v-row>
              <v-row>
                <v-text-field outlined dense v-model="subject" ref="autoFocus" :rules="$_requiredValidation"
                  persistent-hint :label="$t('message.tickets.subject')"></v-text-field>
              </v-row>
              <v-row>
                <v-flex xs12>
                  <label class="subtitle-1">{{ $t('message.tickets.body') }}</label>
                  <ckeditor :editor="editor" v-model="body" :config="editorConfig"></ckeditor>
                </v-flex>
              </v-row>
              <v-row>
                <v-checkbox dense :label="$t('message.common.copy_to_me')" v-model="isCopyToMe" hide-details color="primary"></v-checkbox>
              </v-row>
              <v-row>
                <attach_share v-model="filelist" :filelist="filelist" :sharepoint="attachmentSharepointObj"></attach_share>
              </v-row>
            </v-col>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="success" id="sendMail" :loading="saveLoader" @click="isResend ? resendEmailHandler() : saveAndEmailHandler(4)">{{ $t('message.common.send') }}</v-btn>
            <v-btn color="error" id="close" @click="closeShareDialog">{{ $t('message.common.close') }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>
    <!-- Confirmation about EHF -->
    <v-dialog v-model="ehfWarningObj.dialog" width="400" persistent>
      <v-card>
        <v-card-title>
          {{$t('message.invoice.confirmation')}}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          {{$t('message.invoice.likeToSendEHF')}}
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-layout>
            <v-flex class="text-center">
              <v-btn color="primary" :loading="ehfWarningObj.loading" @click="ehfWarningObj.onSuccess" class="mr-3"> {{$t('message.common.yes')}}</v-btn>
              <v-btn color="error" @click="ehfWarningObj.onCancel"> {{$t('message.common.no')}}</v-btn>
            </v-flex>
          </v-layout>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Error Dialog -->
    <v-dialog v-model="errorDialog" width="500" persistent>
      <v-card>
        <v-card-title class="error white--text">
          {{$t('message.order.errorOccurred') || 'Error'}}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <p class="error--text">{{ errorMessage }}</p>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" @click="closeErrorDialog">{{$t('message.common.close')}}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>
<script>
/* eslint-disable */
import { mapState } from 'vuex'
import Nordea from '@/mixins/InvoiceNordea/index'
import moment from 'moment'
import Editor from '@/ckeditor'
export default {
  data () {
    return {
      invoiceObj: {
        id: this.$route.params.invoice_id ? this.$route.params.invoice_id : 0,
        invoice_date: moment().format('DD.MM.YYYY'),
        due_date: moment().add(10, 'days').format('DD.MM.YYYY'),
        completed_date: '',
        payment_term: 0,
        status: 'draft',
        loading: false
      },
      attachmentsCountLoading: false,
      attachmentsCount: 0,
      disableFields: false,
      splDisableFields: false,
      invoicedDatePicker: moment().format('YYYY-MM-DD'),
      invoiceDuePicker: moment().add(10, 'days').format('YYYY-MM-DD'),
      invoicedCompletedPicker: null,
      invoicedDate: false,
      invoiceDueDate: false,
      invoicedCompletedDate: false,
      dropdownItems: [
        { text: this.$t('message.invoice.saveAsDraft'), id: 0, callback: () => this.saveAsHandler(1, '') },
        { text: this.$t('message.invoice.saveAndDownloadPDF'), id: 2, callback: () => this.saveAsHandler(2, '') }
      ],
      filelist: [],
      attachmentDialog: false,
      attachmentLoading: false,
      sharepointObj: {
        loading: false,
        files: []
      },
      selectedAttachment: [],
      listOfInvoiceItems: [],
      totalValues: {},
      saveLoader: false,
      loading: false,
      listOfCustomers: [],
      previewLoader: false,
      creatingCreditNote: false,
      listOfSearchedProducts: [],
      activeProduct: '',
      getCustomerId: null,
      confirmDialog: false,
      MODULE_URL: 'invoices',
      vatList: [],
      exportLoading: false,
      parentCheck: false,
      isChildSelected: false,
      isAkontoSelected: false,
      disableActions: true,
      disableAkonto: false,
      showDeleteDialog: false,
      deleteLoading: false,
      saveInvoiceItemsModel: [],
      addNewGroupCount: 0,
      headersForInvoiceItems: [],
      ehfLoader: false,
      emailIds: '',
      listOfOrderChecklist: [],
      listOfAllOrderedChecklist: [],
      showEmailDialog: false,
      listOfUnits: [],
      usersList: [],
      selectExceptAkonto: false,
      draggedItem: {},
      isChecklistSelected: false,
      listOfVAT: [{
        text: '0%',
        value: 0
      }, {
        text: '12%',
        value: 12
      }, {
        text: '25%',
        value: 25
      }],
      nordeaLoading: false,
      settingsObj: {},
      editor: Editor,
      editorConfig: {
        toolbar: {
          items: [
            'heading',
            '|',
            'bold',
            'italic',
            '|',
            'bulletedList',
            'numberedList',
            '|',
            'undo',
            'redo'
          ]
        }
      },
      createNoteCreateOptions: [
        { text: 'message.invoice.fullCredit', value: 1 },
        { text: 'message.invoice.partialCredit', value: 2 }
      ],
      createNoteProductsDialog: false,
      allInvoiceItems: [],
      creditNoteDialog: false,
      createButtonEnabled: false,
      isAllCredited: true,
      shareDialog: false,
      subject: '',
      body: '',
      isCopyToMe: false,
      attachmentSharepointObj: {
        loading: false,
        files: []
      },
      emailLoader: false,
      syncToErpLoading: false,
      ehfWarningObj: {
        dialog: false,
        loading: false,
        onSuccess: () => false,
        onCancel: () => false
      },
      errorDialog: false,
      errorMessage: ''
    }
  },
  watch: {
    invoicedDatePicker (val) {
      if (val) {
        this.invoiceObj.invoice_date = this.$formatter.formatDate(val, 'YYYY-MM-DD', 'DD.MM.YYYY')
      }
    },
    invoiceDuePicker (val) {
      if (val) {
        this.invoiceObj.due_date = this.$formatter.formatDate(val, 'YYYY-MM-DD', 'DD.MM.YYYY')
      }
    },
    invoicedCompletedPicker (val) {
      if (val) {
        this.invoiceObj.completed_date = this.$formatter.formatDate(val, 'YYYY-MM-DD', 'DD.MM.YYYY')
      }
    },
    'invoiceObj.payment_term' (val) {
      if (this.invoiceObj.invoice_date) {
        this.invoiceDuePicker = moment(this.invoiceObj.invoice_date, 'DD.MM.YYYY', 'DD.MM.YYYY').add(val, 'days').format('YYYY-MM-DD').toString()
      }
    },
    'shareDialog' (val) {
      this.attachmentSharepointObj.files = []
      if (val) {
        if (this.invoiceObj.order_sharepoint_id) this.getAllDocumentsFromSharepointForShare(this.invoiceObj.order_sharepoint_id)
        this.filelist = []
        const { mail_subject, mail_body, mail_signature } = this.$formatter.cloneVariable(this.settingsObj)
        this.subject = mail_subject
        this.body = `${mail_body} ${mail_signature}`
        // let mailTemplate = getInvoiceTemplate(this.invoiceObj);
        // this.subject = mailTemplate.subject
        // this.body = mailTemplate.body
      }
    }
  },
  components: {
    'attach_share': () => import('@/components/AttachFileToMail.vue')
  },
  mixins: [Nordea],
  computed: {
    ...mapState(['listOfUsers', 'listOfPaymentTerms', 'listOfDeliveryTerms']),
    getStatusName () {
      if (this.invoiceObj.status) {
        let result = this.invoiceStatus.find(x => x.value === this.invoiceObj.status)
        if (result) return `(${result.text})`
        else return ''
      }
    },
    getInvoiceNumber (){
      return this.invoiceObj.number
    },
    productHeaders: {
      get () {
        const heads = [{
          text: this.$t('message.warehouse.product'),
          value: 'product_number',
          width: '6%',
          reportWidth: '9%',
          class: 'text-left'
        }, {
          text: this.$t('message.customer.description'),
          value: 'description',
          reportWidth: '30%',
          width: '10%',
          class: 'text-left'
        }, {
          text: this.$t('message.invoice.qty'),
          value: 'qty',
          showRequired: true,
          reportWidth: '7%',
          width: '5%',
          class: 'text-right'
        }, {
          text: this.$t('message.product.unit'),
          value: 'productunit_name',
          reportWidth: '7%',
          width: '5%',
          class: 'text-right'
        }, {
          text: this.$t('message.product.costPrice'),
          value: 'cost_price',
          showRequired: true,
          reportWidth: '10%',
          width: '10%',
          class: 'text-right'
        }, {
          text: this.$t('message.product.salesPrice'),
          showRequired: true,
          value: 'base_price',
          reportWidth: '10%',
          width: '10%',
          class: 'text-right'
        }, {
          text: this.$t('message.product.discount'),
          value: 'discount',
          width: '6%',
          reportWidth: '5%',
          class: 'text-right'
        }, {
          text: this.$t('message.invoice.sumeksMva'),
          value: 'sum_ex_vat',
          width: '11%',
          reportWidth: '10%',
          class: 'text-right'
        }, {
          text: `${this.$t('message.customer.vatValue')} %`,
          value: 'vat',
          width: '6%',
          reportWidth: '5%',
          class: 'text-right'
        }, {
          text: this.$t('message.invoice.priceIncVat'),
          value: 'price',
          width: '11%',
          reportWidth: '12%',
          class: 'text-right'
        }, {
          text: this.$t('message.document.cost'),
          value: 'cost',
          width: '10%',
          class: 'text-right'
        }, {
          text: 'DB',
          value: 'db',
          width: '10%',
          class: 'text-right'
        }, {
          text: 'DG',
          value: 'dg',
          width: '10%',
          class: 'text-right'
        }]
        this.headersForInvoiceItems = heads
        return this.headersForInvoiceItems
      },
      set (value) {
        this.headersForInvoiceItems = value
      }
    },
    creditNoteOptions () {
      return [
        {
          color: 'primary darken-1',
          icon: 'mdi-check-all',
          title: this.$t('message.invoice.fullCredit'),
          content: this.$t('message.invoice.fullCreditContent'),
          type: 'full'
        },
        {
          color: 'primary',
          icon: 'mdi-checkbox-marked-circle-plus-outline',
          title: this.$t('message.invoice.partialCredit'),
          content: this.$t('message.invoice.partialCreditContent'),
          type: 'partial'
        },
        {
          color: 'error darken-1',
          icon: 'mdi-close',
          title: this.$t('message.invoice.abort'),
          content: this.$t('message.common.cancel'),
          type: 'abort'
        },
      ]
    }
  },
  mounted () {
    if (this.$route.params.invoice_id) {
      this.getSingleRecordHandler()
      this.getInvoiceSettings()
    } else {
      this.getCustomers('isactive eq 1')
      this.getUsersList()
      this.getOurReferenceValueFromContact()
    }
    this.getUnitsList()
    let text = this.$t('message.common.deleteMsg')
    this.$eventBus.$on('deleteSuccess', (data) => {
      if (data === this.MODULE_URL) {
        this.$root.$emit('snackbar', { snackbar: true, color: 'success', text })
        this.$store.commit('hideDialog')
        this.getSingleRecordHandler()
      }
    })
    if (this.getDraftInvoiceId) this.$store.commit('isChangesMade', true)
    else {
      this.$store.commit('isChangesMade', false)
    }

    this.$store.dispatch('getPaymentTerms', this.$t('message.common.days'))
    this.$store.dispatch('getDeliveryTerms')
  },
  methods: {
    checkboxChanged () {
      let selectedProducts = this.allInvoiceItems.filter(x => x.isSelected)
      this.createButtonEnabled = selectedProducts && selectedProducts.length
    },
    getCustomers (filter) {
      return new Promise((resolve, reject) => {
        let query = {
          select: ['id', 'name', 'paymentmethod1', 'paymentmethod2', 'email', 'invoiceemail', 'vat'],
          filter: filter,
          order_by: { field: 'name', asc_or_desc: 'asc' }
        }
        this.$_executeApi('post', 'customers/query', query)
          .then((response) => {
            this.listOfCustomers = response.data
            resolve()
          })
      })
    },
    getOurReferenceValueFromContact () {
      const model = {
        // expand: [{
        //   select: ['firstname', 'lastname'],
        //   model: 'Contact',
        //   on_field: 'Id',
        //   on_parent_field: 'contact_id'
        // }],
        filter: `id eq ${this.$route.params.order_id}`
      }
      this.$_executeApi('post', 'orders/query', model)
        .then((response) => {
          let getContact = response.data && response.data[0] ? response.data[0] : {}
          let invoice = this.$formatter.cloneVariable(this.invoiceObj)
          getContact.ordercustomercontactdata = getContact.ordercustomercontactdata ? JSON.parse(getContact.ordercustomercontactdata) : {}
          invoice.your_contact = getContact.contact_lastname ? `${getContact.ordercustomercontactdata.FirstName || ''} ${getContact.ordercustomercontactdata.LastName || ''}` : getContact.ordercustomercontactdata.FirstName || ''
          invoice.your_reference = getContact.your_reference
          invoice.invoicetodetail = invoice.invoicetodetail ? invoice.invoicetodetail.replace(/<br\s*[\/]?>/gi, "\n") : ''
          invoice.orderfordetail = invoice.orderfordetail ? invoice.orderfordetail.replace(/<br\s*[\/]?>/gi, "\n") : ''
          this.invoiceObj = invoice
        })
    },
    getUsersList (obj) {
      this.$store.dispatch('getUsers', obj).then((result) => {
        if (!this.$route.params.invoice_id) {
          this.$set(this.invoiceObj, 'our_reference', this.$store.state.auth.userDetails.id)
        }
        // getting users
        if (this.invoiceObj.id) {
          let detailsData = this.invoiceObj.ourreferencedata ? JSON.parse(this.invoiceObj.ourreferencedata) : {}
          this.usersList = result
          this.usersList.map(x => {
            if (x.id === this.invoiceObj.our_reference && this.invoiceObj.ourreferencedata) {
              let referenceDetail = [detailsData.FirstName || '', detailsData.LastName || '']
              x.name = referenceDetail.filter(y => y).join(' ')
            } else {
              let referenceDetail = [x.firstname || '', x.lastname || '']
              x.name = referenceDetail.filter(y => y).join(' ')
            }
            return x
          })
        }
      })
    },
    async setInvoiceDownloadActions() {
      if (this.invoiceObj.status !== 'completed') {
        let result = this.listOfCustomers.find(x => x.id === this.invoiceObj.customer_id)
        let alreadyPushed = this.dropdownItems.find(x => x.id === 1)
        if (result && !alreadyPushed) {
          if (result.paymentmethod2 == '2') {
            // check if Org.no is registered with elma, if yes then enable ehf option!
            const ehfOption = { text: this.$t('message.invoice.saveAndUseEHF'), id: 1, callback: () => this.saveAsHandler(3, '') }
            const organizationExists = await this.$_checkOrganzationExistsInElma(result.vat)
            if (organizationExists) {
              this.dropdownItems.push(ehfOption)
            }
            this.dropdownItems.splice(2, 0, { text: this.$t('message.invoice.saveAndEmail'), id: 2, callback: () => this.saveAsHandler(4, '') })
          } else this.dropdownItems.splice(1, 0, { text: this.$t('message.invoice.saveAndEmail'), id: 1, callback: () => this.saveAsHandler(4, '') })
        }
      }
    },
    doCalculation (index) {
      let item = this.$formatter.cloneVariable(this.listOfInvoiceItems)
      if (item[index].qty != null && item[index].qty != '' && item[index].base_price != null && item[index].base_price != '') {
        let model = this.$formatter.formatAndParseModel(item[index], ['qty', 'base_price', 'net_price', 'sum_ex_vat', 'price', 'discount', 'db', 'cost', 'dg'], 'replaceCommaWithDot')
        let total =  model.base_price *  model.qty * (1 - (model.discount || 0) / 100)
        model.sum_ex_vat = total
        let vatValue = (model.vat || 0) / 100
        model.price = total * (1 + vatValue)
        if (model.qty) {
          let getProp = model.is_hour_or_usedproduct ? 'entered_qty' : 'qty'
          model.cost = (model.net_price || 0) * model[getProp]
          model.db =  model.sum_ex_vat - model.cost
        }
        model.dg = (model.db * 100) / model.sum_ex_vat
        let transformData = { number: { list: ['qty', 'base_price', 'net_price', 'discount', 'sum_ex_vat', 'price', 'db', 'cost', 'dg'], format: 'replaceDotWithComma' } }
        model = this.$_formatModel(model, transformData)
        this.$set(this.listOfInvoiceItems, index, model)
        this.calculateAllValues()
        this.$store.commit('isChangesMade', true)
      }
    },
    calculateAllValues () {
      let listOfProducts = this.$formatter.cloneVariable(this.listOfInvoiceItems)
      listOfProducts = this.$formatter.formatAndParseList(listOfProducts, ['qty', 'base_price', 'net_price', 'price', 'discount', 'sum_ex_vat', 'cost', 'db', 'dg'], 'replaceCommaWithDot')
      let totalValues = { sum: 0, mva: 0, invoiceTotal: 0, vat: 25, cost: 0, db: 0, dg: 0 }
      let calculateMVA = 0
      listOfProducts.forEach(element => {
        totalValues.sum += element.sum_ex_vat ? this.$formatter.getRoundedNumberWithPrecision(element.sum_ex_vat) : 0
        totalValues.cost += element.cost ? this.$formatter.getRoundedNumberWithPrecision(element.cost) : 0
        totalValues.db += element.db ? this.$formatter.getRoundedNumberWithPrecision(element.db) : 0
        calculateMVA += this.$formatter.getRoundedNumberWithPrecision((((element.sum_ex_vat || 0) * (element.vat || 0)) / 100))
      })
      // make sure we round with 2 decimal

      totalValues.sum = this.$formatter.getRoundedNumberWithPrecision(totalValues.sum)
      totalValues.mva = this.$formatter.getRoundedNumberWithPrecision(calculateMVA)
      totalValues.db = this.$formatter.getRoundedNumberWithPrecision(totalValues.db)
      totalValues.cost = this.$formatter.getRoundedNumberWithPrecision(totalValues.cost)
      // totalValues.mva = calculateMVA
      totalValues.dg = ((totalValues.db * 100) / (totalValues.sum))

      let grandTotal = this.$formatter.getRoundedNumberWithPrecision(totalValues.sum + totalValues.mva)
      var roundOffValue = this.$formatter.getRoundedNumberWithPrecision(grandTotal, 0) - grandTotal
      totalValues.rounding_value = roundOffValue
      totalValues.invoiceTotal = grandTotal //> 0 ? Math.floor(grandTotal) : Math.ceil(grandTotal)
      totalValues.dg_percentage = totalValues.dg
      let transformData = { number: { list: ['sum', 'mva', 'invoiceTotal', 'cost', 'db', 'rounding_value' , 'dg_percentage'], format: 'replaceDotWithComma' } }
      totalValues = this.$_formatModel(totalValues, transformData)
      this.totalValues = totalValues
    },
    // Searching products
    loadProducts (term, event) {
      if ([30, 32, 37, 38, 39, 40].indexOf(event.keyCode) > -1) return
      let searchText = this.$refs[term].lazySearch
      if (searchText !== null) {
        let model = {
          'expand': [{
            'select': ['id', 'name'],
            'model': 'ProductUnit',
            'on_field': 'id',
            'on_parent_field': 'unitid'
          }],
          'filter': `number contains '${searchText}' OR name contains '${searchText}'`,
          'additionalfilters': [`isactive eq 1`],
          'skip': 0,
          'limit': 50
        }
        this.$_executeApi('post', 'products/query', model).then((response) => {
          this.listOfSearchedProducts = []
          if (response.data.length > 0) {
            response.data = response.data.filter(x => !x.isdeleted)
            response.data.forEach(element => {
              element.productName = element.number ? `${element.number} - ${element.name}` : element.name
            })
            this.listOfSearchedProducts = response.data
          }
        })
      }
    },
    getUnitHandler () {
      let product = this.activeProduct ? this.listOfSearchedProducts.find(x => x.id === this.activeProduct.id) :null
      let discount = this.invoiceObj.customer_supply_discount_rate ? this.$formatter.replaceDotWithComma(this.invoiceObj.customer_supply_discount_rate) : '0,00'
      if (product) {
        let basePrice = this.$formatter.replaceDotWithComma(product.saleprice)
        let costPrice = this.$formatter.replaceDotWithComma(product.netprice)
        let netPrice = product.netprice
        this.listOfInvoiceItems.push({ show_comments: false, product_number: product.number, product_id: product.id, vat: 25,
          discount, net_price: costPrice, base_price: basePrice || '', price: '', unit_id: product.productunit_id || '', description: product.name || '' })
        this.listOfSearchedProducts = []
        this.activeProduct = ''
      } else {
        this.listOfInvoiceItems.push({ show_comments: false, product_number: '', description: this.$refs.searchInput.lazySearch, discount, base_price: '', net_price: '', vat: 25 })
        this.listOfSearchedProducts = []
        this.$refs.searchInput.lazySearch = ''
        this.activeProduct = ''
      }
    },
    isShowResendEhf () {
      let result = this.listOfCustomers.find(x => x.id === this.invoiceObj.customer_id)
      if (result) return result.paymentmethod2 === '2' && this.invoiceObj.completion_type === 3 ? true : false
    },
    isShowResendEmail () {
      let result = this.listOfCustomers.find(x => x.id === this.invoiceObj.customer_id)
      if (result) return result.paymentmethod2 === '2' && (this.invoiceObj.completion_type === 4 || this.invoiceObj.completion_type === 2) ? true : false
    },
    closeDeleteDialog () {
      this.showDeleteDialog = false
    },
    getSingleRecordHandler () {
      // let query = { filter: `id eq ${this.invoiceObj.id}` }
      this.isChecklistSelected = false
      let query = {
        expand: [{
          'select': ['firstname', 'lastname'],
          'model': 'User',
          'on_field': 'Id',
          'on_parent_field': 'Modified_By'
        }, {
          'select': ['firstname', 'lastname'],
          'model': 'User',
          'on_field': 'id',
          'on_parent_field': 'ehfsentby'
        }, {
          'select': ['name'],
          'model': 'Customer',
          'on_field': 'id',
          'on_parent_field': 'customer_id'
        }, {
          'select': ['salesreference', 'sharepoint_id', 'yourordernumber'],
          'model': 'Order',
          'on_field': 'id',
          'on_parent_field': 'order_id'
        }],
        filter: `id eq ${this.invoiceObj.id}`
      }
      this.loading = true
      this.$_executeApi('post', `${this.MODULE_URL}/query`, query).then((response) => {
        if (response && response.data) {
          this.selectExceptAkonto = false
          this.disableActions = true
          let invoiceObj = response.data[0]
          let transformData = { date: { list: ['invoice_date', 'due_date', 'completed_date'], from: 'DD.MM.YYYYTHH:mm:ss', to: 'DD.MM.YYYY' }, number: { list: ['total_sum', 'grand_total', 'invoiceTotal', 'rounding_value' , 'dg_percentage'], format: 'replaceCommaWithDot' } }
          let model = this.$formatter.cloneVariable(invoiceObj)
          model = this.$_formatModel(model, transformData)
          this.getUsersList({ id: [ model.our_reference ] })
          this.getChildrenCount(model.id)
          model.invoicetodetail = model.invoicetodetail ? model.invoicetodetail.replace(/<br\s*[\/]?>/gi, '\n') : ''
          model.orderfordetail = model.orderfordetail ? model.orderfordetail.replace(/<br\s*[\/]?>/gi, '\n') : ''
          if (!model.tasks_performed) model.tasks_performed = ''
          if (!model.comments) model.comments = ''
          this.invoiceObj = model
          this.getCustomerId = this.invoiceObj.customer_id
          if (this.invoiceObj.status !== 'draft' || this.$route.query.from === 'inv') this.disableFields = this.splDisableFields = true
          if (this.$route.query.from === 'inv') this.disableAkonto = this.disableActions = true
          this.getCustomers(`isactive eq 1 or id eq ${model.customer_id}`)
            .then(() => {
              // let result = this.listOfCustomers.find(x => x.id === model.customer_id)
              // if (result && !this.dropdownItems.some(item => item.id === 1)) {
              //   if (result.paymentmethod2 === '2') {
              //     this.dropdownItems.splice(1, 0, { text: this.$t('message.invoice.saveAndUseEHF'), id: 1, callback: () => this.saveAsHandler(3, '') })
              //     this.dropdownItems.splice(2, 0, { text: this.$t('message.invoice.saveAndEmail'), id: 2, callback: () => this.saveAsHandler(4, '') })
              //   } else this.dropdownItems.splice(1, 0, { text: this.$t('message.invoice.saveAndEmail'), id: 1, callback: () => this.saveAsHandler(4, '') })
              // }
              this.setInvoiceDownloadActions()
            })
          this.getInvoiceItems(model.id)
        }
      })
    },
    getInvoiceItems (id) {
      if (id) {
        this.loading = true
        let query = {
          'filter': `invoice_id eq ${id}`,
          'expand': [{
            'select': ['number', 'name', 'unitid', 'isinformation'],
            'model': 'Product',
            'on_field': 'Id',
            'on_parent_field': 'Product_Id'
          }, {
            'select': ['name'],
            'model': 'ProductUnit',
            'on_field': 'Id',
            'on_parent_field': 'Unit_Id'
          }],
          'order_by': {
            field: 'alignindex',
            asc_or_desc: 'asc'
          }
        }
        this.$_executeApi('post', `invoiceitems/query`, query).then((response) => {
          if (response.data.length > 0) {
            let invoiceItems = response.data
            let transformData = { number: { list: ['qty', 'base_price', 'net_price', 'price', 'discount', 'sum_ex_vat', 'cost', 'db', 'dg'], format: 'replaceDotWithComma' } }
            let model = this.$formatter.cloneVariable(invoiceItems)
            model = this.$_formatListModel(model, transformData)
            model.forEach(element => {
              element.show_comments = !!element.comments
            })
            this.listOfInvoiceItems = model
            this.isAllCredited = this.listOfInvoiceItems.every(x => x.iscredited)
            this.calculateAllValues()
            this.getInvoicechecklist()
          }
          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
      } else this.loading = false
    },
    getUnitsList () {
      this.$_executeApi('post', `productunits/query`, { filter: 'isactive eq 1' })
        .then(resposnse => {
            this.listOfUnits = resposnse.data
        })
    },
    async downloadInvoice (fromSendEmail, emailId) {
      this.previewLoader = true
      if (this.invoiceObj.id && this.invoiceObj.status === 'draft' && this.$store.state.isChangesMade) {
        await this.saveInvoiceModel()
      }
      let getBase64Content = await this.getInvoiceReportContent()
      const customer = this.invoiceObj.customerdata ? JSON.parse(this.invoiceObj.customerdata || {}) : {}
      const customerLang = customer.Language ? customer.Language : this.$i18n.locale
      let lang = this.$i18n.messages[customerLang].message
      let model = { content: getBase64Content, filename: `${lang.customer.invoice}_${this.invoiceObj.number ? `_${this.invoiceObj.number}` : ''}.pdf` }
      return this.$_executeApi('post', `${this.MODULE_URL}/generate_pdf/${this.invoiceObj.id}`, model)
        .then(async (response) => {
          let fileName = response.data
          if (!fromSendEmail) {
            window.open(`${process.env.VUE_APP_URL}file/download?fileName=${fileName}`, '_blank')
          } else {
            const orderdata = this.invoiceObj.orderdata ? JSON.parse(this.invoiceObj.orderdata || {}) : {}
            if (this.subject) {
              this.subject = this.subject
                .replace('{{invoice_number}}', this.invoiceObj.number || '')
                .replace('{{your_reference}}', this.invoiceObj.your_reference || '')
                .replace('{{your_order_number}}', orderdata.YourOrderNumber || '')
            }
            if (this.body) {
              this.body = this.body
                .replace('{{invoice_number}}', this.invoiceObj.number || '')
                .replace('{{your_reference}}', this.invoiceObj.your_reference || '')
                .replace('{{your_order_number}}', orderdata.YourOrderNumber || '')
            }
            let currentUserMail = this.$store.state.userDetails ? this.$store.state.userDetails.email : ''
            let model = {
              'to': emailId,
              'subject': this.subject,
              'filename': fileName,
              'body': this.body,
              'cc': this.isCopyToMe && currentUserMail ? [currentUserMail] : [],
              'visiblefilename': `Faktura_${this.invoiceObj.number ? `_${this.invoiceObj.number}` : ''}.pdf`
            }
            // upload files to local for share attachments
            const formData = new FormData()
            const files = this.filelist
            files.forEach(file => {
              if (!file.is_sharepoint) {
                formData.append(file.name, file)
              } else {
                formData.append(file.id, file)
              }
            })
            if (files && files.length > 0) {
              await this.$_executeApi('post', `warehouseorders/attachment_upload/${this.invoiceObj.id}/INVOICE`, formData)
            }
            return this.$_executeApi('post', `${this.MODULE_URL}/share/${this.invoiceObj.id}`, model)
              .then((result) => {
                if (result.data && result.data.status === 500) {
                  this.$parent.$parent.mailOrEHF = 1
                  this.$parent.$parent.invoiceIssueModal = true
                }
                if (result.data) {
                  const { invoice } = result.data
                  if (invoice) {
                    let transformData = { date: { list: ['invoice_date', 'due_date', 'completed_date'], from: 'DD.MM.YYYYTHH:mm:ss', to: 'DD.MM.YYYY' }, number: { list: ['total_sum', 'grand_total', 'invoiceTotal', 'rounding_value' , 'dg_percentage'], format: 'replaceCommaWithDot' } }
                    let model = this.$formatter.cloneVariable(invoice)
                    model = this.$_formatModel(model, transformData)
                    this.invoiceObj = model
                  }
                }
              })
          }
        })
        .finally(() => {
          this.previewLoader = false
        })
    },
    getCreditnoteconfirmation (type) {
      this.confirmDialog = true
    },
    createCreditNote (type) {
      if (type === 'full') {
        this.$router.push({ path: `/orders/edit/${this.$route.params.order_id}/invoice/creditnote`, query: { invoice_id: this.invoiceObj.id } })
      } else if (type === 'partial') {
        this.creditNoteDialog = false
        let invoiceItems = this.$formatter.cloneVariable(this.listOfInvoiceItems)
        this.allInvoiceItems = invoiceItems.filter(x => !x.iscredited) // show only not credited products
        this.createNoteProductsDialog = true
      }
    },
    getInvoiceReportContent () {
      return this.exportToNordea([this.invoiceObj.id || 0], '', true, [])
    },
    getCustomerAddress (id) {
      return this.$_executeApi('get', `customeraddresses/get_first_or_default/${id}`)
    },
    async saveAsHandler (type, emailIds) {
      if (this.$refs.invoiceForm.validate()) {
        if (type === 4 && !emailIds) {
          var emailId = ''
          this.isResend = false
          let result = this.listOfCustomers.find(x => x.id === this.invoiceObj.customer_id)
          if (result) emailId = result.invoiceemail ? result.invoiceemail : ''
          this.emailIds = emailId
          this.shareDialog = true
        } else {
          this.saveLoader = true
          let invoiceModel = this.$formatter.cloneVariable(this.invoiceObj)
          invoiceModel.invoice_date = `${invoiceModel.invoice_date}T${moment().format('HH:mm:ss')}`
          let transformData = { date: { list: ['invoice_date', 'due_date', 'completed_date'], from: 'DD.MM.YYYYTHH:mm:ss', to: 'YYYY-MM-DDTHH:mm:ss' } }
          let model = this.$formatter.cloneVariable(invoiceModel)
          model.order_id = this.$route.params.order_id
          model.total_sum = this.$formatter.replaceCommaWithDot(this.totalValues.sum)
          model.grand_total = this.$formatter.replaceCommaWithDot(this.totalValues.invoiceTotal)
          model.rounding_value = this.$formatter.replaceCommaWithDot(this.totalValues.rounding_value)
          model.dg_percentage = this.$formatter.replaceCommaWithDot(this.totalValues.dg_percentage)
          model.dg_sum = this.$formatter.replaceCommaWithDot(this.totalValues.db)
          model = this.$_formatModel(model, transformData)
          model.type = 1
          switch (type) {
            case 1: // Save as Draft
              model.completion_type = 1
              model.status = 'draft'
              break
            case 2: // Save and Download
              model.completion_type = 2
              model.status = 'completed'
              break
            case 3: // Save and Use EHF
              model.completion_type = 3
              model.status = 'completed'
              break
            case 4: // Save and Send
              model.completion_type = 4
              model.status = 'completed'
              break
            default:
              break
          }
          if (!model.id) {
            this.saveInvoiceItemInit(model, type, emailIds)
          } else {
            // let canSaveInvoice = true
            // let sum = +this.$formatter.replaceCommaWithDot(this.totalValues.invoiceTotal || 0)
            // if (sum < 1) canSaveInvoice = false
            // if (canSaveInvoice) {
            this.$_saveUpdateHandler(this.MODULE_URL, model)
              .then(async (response) => {
                this.$store.commit('isChangesMade', false)
                let orderSaleReference = this.invoiceObj.order_salesreference || ''
                let orderYourOrderNum = this.invoiceObj.order_yourordernumber || ''
                let transformData = { date: { list: ['invoice_date', 'due_date', 'completed_date'], from: 'DD.MM.YYYY', to: 'DD.MM.YYYY' } }
                let invoiceObj = this.$_formatModel(response, transformData)
                invoiceObj.invoicetodetail = invoiceObj.invoicetodetail ? invoiceObj.invoicetodetail.replace(/<br\s*[\/]?>/gi, '\n') : ''
                invoiceObj.orderfordetail = invoiceObj.orderfordetail ? invoiceObj.orderfordetail.replace(/<br\s*[\/]?>/gi, '\n') : ''
                invoiceObj.order_salesreference = orderSaleReference
                invoiceObj.order_yourordernumber = orderYourOrderNum
                this.invoiceObj = invoiceObj || {}
                if (this.invoiceObj.status !== 'draft' || this.$route.query.from === 'inv') this.disableFields = this.splDisableFields = true
                if (this.listOfInvoiceItems.length > 0) {
                  try {
                    await this.saveOrUpdateInvoiceItems(response.id)
                  } catch (err) {
                    this.saveLoader = false
                    return
                  }
                  if (type === 4) {
                    this.shareDialog = false
                    this.sendEmailHandler(emailIds)
                  }
                  if (type === 2) {
                    this.downloadInvoice()
                  }
                  if (type === 3) {
                    let getBase64Content = await this.getInvoiceReportContent()
                    const customer = this.invoiceObj.customerdata ? JSON.parse(this.invoiceObj.customerdata || {}) : {}
                    const customerLang = customer.Language ? customer.Language : this.$i18n.locale
                    let lang = this.$i18n.messages[customerLang].message
                    let model = { content: getBase64Content, filename: `${lang.customer.invoice}_${this.invoiceObj.number ? `_${this.invoiceObj.number}` : ''}.pdf` }
                    return this.$_executeApi('post', `${this.MODULE_URL}/generate_pdf/${this.invoiceObj.id}`, model)
                      .then(async (response) => {
                        let fileName = response.data
                        let ehfModel = { id: this.invoiceObj.id, type: 1, attachment_filename: fileName }
                        await this.generateEHFReport(ehfModel)
                      })
                  }
                }
                this.saveLoader = false
              }).catch(() => {
                this.saveLoader = false
              })
            // }
            // else {
              // this.saveLoader = false
              // this.$root.$emit('snackbar', { snackbar: true, color: 'error', text: this.$t('message.invoice.cannotCreateInvoice') })
            // }
          }
        }
      } else {
        this.$root.$emit('snackbar', { snackbar: true, color: 'error', text: this.$t('message.common.validationIssue') })
        setTimeout(() => {
          var x = document.querySelectorAll(".invoiceitems .v-input--has-state")
          x.forEach(element => {
            element.style.background = "#ff00006e"
          })
        })
      }
    },
    async saveActions (response, emailIds, type) {
      this.$store.commit('isChangesMade', false)
      if (this.listOfInvoiceItems.length > 0) {
        this.invoiceObj.id = response.id
        this.invoiceObj.number = response.number
        if (type === 4) {
          this.shareDialog = false
          await this.sendEmailHandler(emailIds)
        }
        if (type === 2) {
          await this.downloadInvoice()
        }
        if (type === 3) {
          let getBase64Content = await this.getInvoiceReportContent()
          const customer = this.invoiceObj.customerdata ? JSON.parse(this.invoiceObj.customerdata || {}) : {}
          const customerLang = customer.Language ? customer.Language : this.$i18n.locale
          let lang = this.$i18n.messages[customerLang].message
          let model = { content: getBase64Content, filename: `${lang.customer.invoice}_${this.invoiceObj.number ? `_${this.invoiceObj.number}` : ''}.pdf` }
          return this.$_executeApi('post', `${this.MODULE_URL}/generate_pdf/${this.invoiceObj.id}`, model)
            .then(async (response) => {
              let fileName = response.data
              let ehfModel = { id: this.invoiceObj.id, type: 1, attachment_filename: fileName }
              await this.generateEHFReport(ehfModel)
            })
        }
      }
    },
    saveAndEmailHandler () {
      if (this.$refs.shareForm.validate() && this.emailIds) {
        if (this.emailIds) this.isValidEmail = true
        const getMailIDsString = this.emailIds
        const listOfMailIds = getMailIDsString.split(',')
        if (listOfMailIds.length > 0) {
          for (let i = 0; i < listOfMailIds.length; i++) {
            if (listOfMailIds[i]) {
              listOfMailIds[i] = listOfMailIds[i].trim()
              if (this.validEmail(listOfMailIds[i])) this.isValidEmail = false
              else this.isValidEmail = true
            }
          }
        }
        if (!this.isValidEmail) {
          this.saveAsHandler(4, listOfMailIds)
        } else this.$root.$emit('snackbar', { snackbar: true, color: 'error', text: this.$t('message.common.validationIssue') })
      } else this.$root.$emit('snackbar', { snackbar: true, color: 'error', text: this.$t('message.common.validationIssue') })
    },
    /* eslint-disable */
    validEmail: function (email) {
      var re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      return re.test(email)
    },
    ehfWarningHandler () {
      this.ehfWarningObj = {
        dialog: true,
        loading: false,
        onSuccess: async () => {
          this.ehfWarningObj.loading = true
          await this.resendEhf()
          this.ehfWarningObj.dialog = false
          this.ehfWarningObj.loading = false
        },
        onCancel: () => {
          this.ehfWarningObj.dialog = false
        }
      }
    },
    async resendEhf () {
      this.ehfLoader = true
      let getBase64Content = await this.getInvoiceReportContent()
      const customer = this.invoiceObj.customerdata ? JSON.parse(this.invoiceObj.customerdata || {}) : {}
      const customerLang = customer.Language ? customer.Language : this.$i18n.locale
      let lang = this.$i18n.messages[customerLang].message
      let model = { content: getBase64Content, filename: `${lang.customer.invoice}_${this.invoiceObj.number ? `_${this.invoiceObj.number}` : ''}.pdf` }
      return this.$_executeApi('post', `${this.MODULE_URL}/generate_pdf/${this.invoiceObj.id}`, model)
        .then(async (response) => {
          let fileName = response.data
          let ehfModel = { id: this.invoiceObj.id, type: 1, attachment_filename: fileName }
          await this.generateEHFReport(ehfModel, true)
        })
    },
    generateEHFReport (ehfModel, fromResend) {
      return this.$_executeApi('post', `${this.MODULE_URL}/generate_ehf`, ehfModel)
        .then(resposnse => {
          if (resposnse.data && resposnse.data.status === 500) {
            this.$parent.$parent.mailOrEHF = 2
            this.$parent.$parent.invoiceIssueModal = true
          }
          if (fromResend) {
            this.$root.$emit('snackbar', { snackbar: true, color: 'success', text: this.$t('message.invoice.ehfInfo') })
            this.ehfLoader = false
          }
          this.getSingleRecordHandler()
      }).finally(() => {
        this.ehfLoader = false
        this.saveLoader = false
      })
    },
    async resendDialogEmailHandler () {
      var emailId = ''
      let result = this.listOfCustomers.find(x => x.id === this.invoiceObj.customer_id)
      if (result) emailId = result.invoiceemail ? result.invoiceemail : ''
      this.emailIds = emailId
      this.isResend = true
      this.shareDialog = true
    },
    async resendEmailHandler () {
      if (this.$refs.shareForm.validate() && this.emailIds) {
        if (this.emailIds) this.isValidEmail = true
        const getMailIDsString = this.emailIds
        const listOfMailIds = getMailIDsString.split(',')
        if (listOfMailIds.length > 0) {
          for (let i = 0; i < listOfMailIds.length; i++) {
            if (listOfMailIds[i]) {
              listOfMailIds[i] = listOfMailIds[i].trim()
              if (this.validEmail(listOfMailIds[i])) this.isValidEmail = false
              else this.isValidEmail = true
            }
          }
        }
        if (!this.isValidEmail) {
          this.saveLoader = true
          await this.sendEmailHandler(listOfMailIds)
          this.saveLoader = false
        } else this.$root.$emit('snackbar', { snackbar: true, color: 'error', text: this.$t('message.common.validationIssue') })
      } else this.$root.$emit('snackbar', { snackbar: true, color: 'error', text: this.$t('message.common.validationIssue') })
    },
    async sendEmailHandler (emailId) {
      if (emailId) {
        await this.downloadInvoice(true, emailId)
      }
    },
    saveOrUpdateInvoiceItems (invoiceId) {
      let invoiceItemsModel = this.$formatter.cloneVariable(this.listOfInvoiceItems)
      let transformData = { number: { list: ['qty', 'base_price', 'net_price', 'price', 'discount', 'sum_ex_vat', 'cost', 'db', 'dg'], format: 'replaceCommaWithDot' } }
      invoiceItemsModel = this.$_formatListModel(invoiceItemsModel, transformData)
      let invoiceItemsJSON = { 'items': this.$formatter.cloneVariable(invoiceItemsModel) }
      return this.$_executeApi('post', `invoiceitems/bulk_save/${invoiceId}`, invoiceItemsJSON).then(response => {
        this.getInvoiceItems(invoiceId)
      }).catch((err) => {
        this.errorMessage = (err && err.response && err.response.data && err.response.data.message) || (err && err.response && err.response.data) || (err && err.message) || 'An error occurred while saving invoice items'
        this.errorDialog = true
        throw err
      })
    },
    async saveInvoiceItemInit (invoiceObj, type, emailIds) {
      let invoiceItemsModel = this.$formatter.cloneVariable(this.listOfInvoiceItems)
      let transformData = { number: { list: ['qty', 'base_price', 'net_price', 'price', 'discount', 'sum_ex_vat', 'cost', 'db', 'dg'], format: 'replaceCommaWithDot' } }
      invoiceItemsModel = this.$_formatListModel(invoiceItemsModel, transformData)
      let model = { invoice: invoiceObj, invoiceitems: invoiceItemsModel }
      this.$_executeApi('post', '/invoices/create_invoice_with_items', model)
        .then(async (response) => {
          let result = response.data ? response.data : {}
          if (type !== 1) await this.saveActions(result, emailIds, type)
          this.saveLoader = false
          this.selectExceptAkonto = false
          this.$router.push({ path: `/orders/edit/${this.$route.params.order_id}/invoice/actions/${result.id}`, query: { invoice_id: result.id } })
        })
        .catch(() => {
          this.saveLoader = false
        })
    },
    invoiceEdited () {
      this.$store.commit('isChangesMade', true)
    },
    saveInvoiceModel () {
      let invoiceModel = this.$formatter.cloneVariable(this.invoiceObj)
      invoiceModel.invoice_date = `${invoiceModel.invoice_date}T${moment().format('HH:mm:ss')}`
      let transformData = { date: { list: ['invoice_date', 'due_date', 'completed_date'], from: 'DD.MM.YYYYTHH:mm:ss', to: 'YYYY-MM-DDTHH:mm:ss' } }
      let model = this.$_formatModel(invoiceModel, transformData)
      return this.$_saveUpdateHandler(this.MODULE_URL, model)
    },
    getUnitValue (id) {
      let getUnits = this.$formatter.cloneVariable(this.listOfUnits)
      let getUnitName = getUnits.find(x => x.id === id)
      if (getUnitName) return getUnitName.name
      else return ''
    },
    hideAndShowComments (element, index) {
      let model = this.listOfInvoiceItems[index]
      model.show_comments = !model.show_comments
      this.$set(this.listOfInvoiceItems, index, model)
    },
    getInvoiceSettings () {
      this.$_getSingleRecordHandler('invoicesettings')
        .then((result) => {
          if (result.data) this.settingsObj = result.data
        })
    },
    getListHandler () { },
    createPartialCreditNote () {
      // let selectedProducts = this.allInvoiceItems.filter(x => x.isSelected).map(x => x.id)
      let selectedProducts = this.allInvoiceItems.flatMap(x => x.isSelected ? [x.id] : []) // is equal filter and map
      // this.$store.commit('setParticalCreditProductIds', selectedProducts)
      /* store it in local storage and clear it in credit note after created */
      window.localStorage.setItem('#credit_note_select_products', JSON.stringify(selectedProducts))
      setTimeout(() => {
        this.$router.push({ path: `/orders/edit/${this.$route.params.order_id}/invoice/creditnote`, query: { invoice_id: this.invoiceObj.id } })
      })
    },
    getChildrenCount (id) {
      this.attachmentsCountLoading = true
      this.$_executeApi('get', `invoices/get_attachment_count/${id}`)
        .then((response) => {
          if (response && response.data) {
            this.attachmentsCount = response.data.length
            this.selectedAttachment = response.data
          }
        }).finally(() => {
          this.attachmentsCountLoading = false
        })
    },
    getAllDocumentsFromSharepoint (id) {
      this.sharepointObj.loading = true
      this.$_executeApi('get', `sharepoint/get_children_documents/${id}`)
        .then((response) => {
          if (response) {
            var files = response.data || []
            if (this.selectedAttachment) {
              files.map(x => {
                if (this.selectedAttachment.find(y => y.sharepointattachmentid === x.id)) {
                  x.isSelected = true
                }
                return x
              })
            }
            this.sharepointObj.files = files
          }
        })
        .finally(() => {
          this.sharepointObj.loading = false
        })
    },
    openAttachmentDialog () {
      this.attachmentDialog = true
      this.sharepointObj.files = []
      this.filelist = []
      if (this.invoiceObj.order_sharepoint_id) {
        this.getAllDocumentsFromSharepoint(this.invoiceObj.order_sharepoint_id)
      }
    },
    closeAttachmentDialog () {
      this.attachmentDialog = false
    },
    addAttachmentsToSP () {
      this.attachmentLoading = true
      const formData = new FormData()
      formData.append('order_id', this.$route.params.order_id)
      formData.append('sharepoint_id', this.invoiceObj.order_sharepoint_id)
      const files = this.filelist
      files.forEach(file => {
        if (!file.is_sharepoint) {
          formData.append(file.name, file)
        } else {
          formData.append(file.id, file)
        }
      })
      this.$_executeApi('post', `invoices/attachment_upload/${this.invoiceObj.id}`, formData)
        .then((response) => {
          if (response) {
            this.getChildrenCount(this.invoiceObj.id)
            this.attachmentDialog = false
          }
        })
        .finally(() => {
          this.attachmentLoading = false
        })
    },
    getAllDocumentsFromSharepointForShare (id) {
      this.attachmentSharepointObj.loading = true
      this.$_executeApi('get', `sharepoint/get_children_documents/${id}`)
        .then((response) => {
          if (response) {
            var files = response.data || []
            this.attachmentSharepointObj.files = files
          }
        })
        .finally(() => {
          this.attachmentSharepointObj.loading = false
        })
    },
    closeShareDialog () {
      this.emailIds = ''
      this.shareDialog = false
    },
    syncToErp () {
      this.syncToErpLoading = true
      this.$_executeApi('post', 'invoices/sync_to_24seven', { ids: [this.invoiceObj.id] })
        .then((response) => {
          this.syncToErpLoading = false
          this.$root.$emit('snackbar', { snackbar: true, color: 'success', text: 'Synced to ERP successfully' })
          this.getSingleRecordHandler()
        })
        .catch((err) => {
          this.syncToErpLoading = false
          const errorMsg = (err && err.response && err.response.data && err.response.data.message) || 'An error occurred while syncing to ERP'
          this.$root.$emit('snackbar', { snackbar: true, color: 'error', text: errorMsg })
        })
    },
    closeErrorDialog () {
      this.errorDialog = false
      this.errorMessage = ''
      window.location.reload()
    }
  },
  beforeRouteLeave (to, from, next) {
    if (!this.$store.state.isChangesMade) {
      next()
    } else {
      this.$store.commit('isChangesMade', true)
      this.$store.commit('changeLostDialog', true)
    }
  },
  beforeDestroy () {
    this.$root.$off('deleteItems')
    this.$eventBus.$off('deleteSuccess')
    this.$root.$off('deleteSuccess')
  }
}
</script>
<style>
.align-right input {
  text-align: right !important;
}
.invoiceProducts textarea {
  margin-top: 2px !important;
}
.invoiceProducts th, .invoiceProducts td {
  padding: 2px !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important;
}
.invoiceProducts .v-input {
  font-size: 14px !important;
}
.invoiceProducts.v-data-table tbody tr:hover:not(.v-data-table__expanded__content) {
  background-color: inherit;
}
.commentsArea .v-input__append-inner {
  margin-top: 5px;
}
.invoiceProducts .v-data-table__wrapper {
  overflow: unset !important;
}
.invoiceProducts .v-text-field--full-width .v-input__append-inner {
  margin-top: 4px !important;
}
.invalidTotal {
  color: #f50909;
}
</style>
